/** @flow */

import {
  GetWellbeingProviders,
  GetWellbeingProvidersSuccess,
  GetWellbeingProvidersError,
  GetMessagesLoginText,
  GetMessagesLoginTextSuccess,
  GetMessagesLoginTextError,
  GetMessagesReleaseVersion,
  GetMessagesReleaseVersionSuccess,
  GetMessagesReleaseVersionError,
} from "@modules/shared/constants/actions";
import { startRequest, finishRequest } from "@store/utils";
import type { PaginatedList } from "@types";
import type { EmployeeType } from "@modules/employee/types";
import config from "@config";
import { cloneObject } from "@utils/object";
import type { WellbeingProvider, SystemMessagesLogin } from "@modules/shared/reducer/types";
import type { ReleaseNote } from "@modules/shared/selectors";

export type HomeState = $Exact<{
  requestsInProgress: number,
  wellbeingProviders: PaginatedList<WellbeingProvider>,
  login: SystemMessagesLogin | null,
  releaseNote: ReleaseNote | null,
}>;

// Initial state
const defaultProviders = {
  content: [],
  pagination: {
    sort: { orders: [] },
    pageNumber: 0,
    pageSize: 0,
    offset: 0,
  },
  totalElements: 0,
};

const defaultLogin = {
  login: null,
};

export const initialState: HomeState = {
  requestsInProgress: 0,
  wellbeingProviders: defaultProviders,
  login: defaultLogin,
  releaseNote: null,
};

const setLoading = (state: HomeState) => () => {
  startRequest(state);
  return state;
};

export const onGetWellbeingProviders = (state: HomeState) => ({
  payload,
}: {
  payload: PaginatedList<WellbeingProvider>,
}) => {
  return {
    ...startRequest(state),
    wellbeingProviders: defaultProviders,
  };
};
export const onGetWellbeingProvidersSuccess = (state: HomeState) => ({
  payload,
}: {
  payload: PaginatedList<WellbeingProvider>,
}) => {
  return {
    ...finishRequest(state),
    wellbeingProviders: payload,
  };
};
export const onGetWellbeingProvidersError = (state: HomeState) => ({
  payload,
}: {
  payload: PaginatedList<WellbeingProvider>,
}) => {
  return {
    ...finishRequest(state),
    wellbeingProviders: defaultProviders,
  };
};

export const onResetSelectedEmployees = (state: HomeState) => () => initialState;

const onGetMessagesLoginTextSuccess = (state: HomeState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: null,
  login: payload,
});

const onGetMessagesLoginTextError = (state: HomeState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: payload,
});

const onGetMessagesReleaseVersionSuccess = (state: HomeState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: null,
  releaseNote: payload,
});

export const onGetMessagesReleaseVersionError = (state: HomeState) => ({ payload }: Object) => ({
  ...finishRequest(state),
  error: payload,
});

export default (state: HomeState = initialState, action: Object): Object => {
  const act = {
    [GetWellbeingProviders]: onGetWellbeingProviders,
    [GetWellbeingProvidersSuccess]: onGetWellbeingProvidersSuccess,
    [GetWellbeingProvidersError]: onGetWellbeingProvidersError,

    // system message and release notes
    [GetMessagesLoginText]: setLoading,
    [GetMessagesLoginTextSuccess]: onGetMessagesLoginTextSuccess,
    [GetMessagesLoginTextError]: onGetMessagesLoginTextError,
    [GetMessagesReleaseVersion]: setLoading,
    [GetMessagesReleaseVersionSuccess]: onGetMessagesReleaseVersionSuccess,
    [GetMessagesReleaseVersionError]: onGetMessagesReleaseVersionError,
  };

  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
